(function () {
  'use strict';

  if (window.CozyProductVideo) {
    window.CozyProductVideo.initAll(document);
    return;
  }

  const SDK_URL = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
  const initialized = new WeakSet();
  let sdkPromise;

  function splitStreamConfig(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return [];

    if (raw.charAt(0) === '[') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(String).map(value => value.trim()).filter(Boolean);
      } catch (error) {
        // A malformed JSON value is handled as a single entry and can still fall back to udook.
      }
    }

    return raw.split(/[\n,]+/).map(value => value.trim()).filter(Boolean);
  }

  function normalizeCustomerCode(rawValue) {
    return String(rawValue || '')
      .trim()
      .replace(/^customer-/i, '')
      .replace(/\.cloudflarestream\.com$/i, '');
  }

  function normalizeStreamUrl(rawValue, rawCustomerCode) {
    const value = String(rawValue || '').trim();
    const customerCode = normalizeCustomerCode(rawCustomerCode);

    if (/^[a-f0-9]{32}$/i.test(value)) {
      if (!/^[a-z0-9]+$/i.test(customerCode)) return '';
      return `https://customer-${customerCode}.cloudflarestream.com/${value}/iframe`;
    }

    let url;
    try {
      url = new URL(value);
    } catch (error) {
      return '';
    }

    if (url.protocol !== 'https:') return '';
    const isCloudflareHost = /^customer-[a-z0-9]+\.cloudflarestream\.com$/i.test(url.hostname);
    const isVideoDeliveryHost = url.hostname.toLowerCase() === 'iframe.videodelivery.net';
    if (!isCloudflareHost && !isVideoDeliveryHost) return '';

    if (isCloudflareHost) {
      url.pathname = url.pathname.replace(/\/(watch|iframe)\/?$/i, '/iframe');
      if (!/\/iframe\/?$/i.test(url.pathname)) return '';
    }

    return url.toString();
  }

  function buildStreamSources(rawConfig, customerCode) {
    return splitStreamConfig(rawConfig)
      .map(value => normalizeStreamUrl(value, customerCode))
      .filter(Boolean);
  }

  function withPlayerOptions(source, multiple) {
    const url = new URL(source);
    url.searchParams.set('muted', 'true');
    url.searchParams.set('preload', 'none');
    if (!multiple) url.searchParams.set('loop', 'true');
    url.searchParams.set('autoplay', 'true');
    return url.toString();
  }

  function ensureStreamSdk() {
    if (window.Stream) return Promise.resolve(window.Stream);
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SDK_URL}"]`);
      const script = existing || document.createElement('script');
      const finish = () => window.Stream ? resolve(window.Stream) : reject(new Error('Cloudflare Stream SDK unavailable'));

      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', reject, { once: true });
      if (!existing) {
        script.src = SDK_URL;
        script.async = true;
        document.head.appendChild(script);
      } else if (window.Stream) {
        finish();
      }
    });

    return sdkPromise;
  }

  function toggleNativeFullscreen(video) {
    if (!video) return;
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;

    if (!fullscreenElement) {
      const request = video.requestFullscreen || video.webkitRequestFullscreen || video.mozRequestFullScreen;
      if (request) request.call(video);
      video.muted = false;
      if (video.volume === 0) video.volume = 1;
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen;
      if (exit) exit.call(document);
      video.muted = true;
      video.volume = 0;
    }
  }

  function initPlayer(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const nativeVideo = root.querySelector('video.cozy-video');
    const streamFrame = root.querySelector('iframe.cozy-stream-video');
    const startButton = root.querySelector('.cozy-video-start');
    const closeButton = root.querySelector('.cozy-video-close');
    const streamSources = buildStreamSources(root.dataset.streamConfig, root.dataset.cloudflareCustomerCode);
    const fallbackVideo = String(root.dataset.fallbackVideoUrl || '').trim();
    const useStream = streamSources.length > 0;
    const sources = useStream ? streamSources : (fallbackVideo ? [fallbackVideo] : []);
    let sourceIndex = 0;
    let streamPlayer = null;

    if (!sources.length) {
      root.hidden = true;
      return;
    }

    function mountNative(index) {
      const source = sources[index];
      if (!source || !nativeVideo) return;
      nativeVideo.innerHTML = '';
      const sourceElement = document.createElement('source');
      sourceElement.src = source;
      sourceElement.type = 'video/mp4';
      nativeVideo.appendChild(sourceElement);
      nativeVideo.hidden = false;
      streamFrame.hidden = true;
      nativeVideo.load();
      const playPromise = nativeVideo.play();
      if (playPromise) playPromise.catch(() => {});
    }

    function mountStream(index) {
      const source = sources[index];
      if (!source || !streamFrame) return;
      streamPlayer = null;
      nativeVideo.hidden = true;
      streamFrame.hidden = false;
      streamFrame.src = withPlayerOptions(source, sources.length > 1);

      ensureStreamSdk().then(Stream => {
        if (!streamFrame.isConnected) return;
        streamPlayer = Stream(streamFrame);
        streamPlayer.addEventListener('ended', () => {
          if (sources.length < 2) return;
          sourceIndex = (sourceIndex + 1) % sources.length;
          mountStream(sourceIndex);
        });
        const playPromise = streamPlayer.play();
        if (playPromise) playPromise.catch(() => {});
      }).catch(() => {
        // The official iframe remains fully operable even if the optional control SDK is unavailable.
      });
    }

    startButton.addEventListener('click', () => {
      startButton.hidden = true;
      root.dataset.cozyVideoLoaded = 'true';
      if (useStream) mountStream(0);
      else mountNative(0);
    }, { once: true });

    closeButton.addEventListener('click', () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
      if (fullscreenElement) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen;
        if (exit) exit.call(document);
      }
      if (streamPlayer && streamPlayer.pause) streamPlayer.pause();
      nativeVideo.pause();
      streamFrame.removeAttribute('src');
      root.classList.add('hidden');
      window.setTimeout(() => root.remove(), 300);
    });

    nativeVideo.addEventListener('click', () => toggleNativeFullscreen(nativeVideo));
    nativeVideo.addEventListener('ended', () => {
      sourceIndex = (sourceIndex + 1) % sources.length;
      mountNative(sourceIndex);
    });
    nativeVideo.addEventListener('error', () => {
      if (sources.length < 2) return;
      sourceIndex = (sourceIndex + 1) % sources.length;
      mountNative(sourceIndex);
    });

    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
      if (!fullscreenElement && !nativeVideo.hidden) {
        nativeVideo.muted = true;
        nativeVideo.volume = 0;
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-cozy-video-player]').forEach(initPlayer);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);

  document.addEventListener('shopify:section:load', event => initAll(event.target));

  window.CozyProductVideo = {
    splitStreamConfig,
    normalizeStreamUrl,
    buildStreamSources,
    initAll
  };
})();
